import { Box } from "@mui/material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useState, useEffect, useMemo } from "react";
import { useLocation, useBlocker, useSearchParams } from "react-router-dom";
import { useGetPermissionsQuery } from "src/shared/api/rbac/rbac";
import { TablePage } from "src/shared/components/Table";
import { TableToolbar } from "src/shared/components/Table/ui/TableToolbar";
import { companiesTabsOptions } from "src/shared/constants/companiesTabs.constants";
import { useGetReportsQuery } from "src/shared/hooks/reports/reports.hooks";
import { IBaseQueryParams } from "src/shared/types/query";
import {
  useGetDraftReportsListQuery,
  useUpdateBonusItemsMutation,
  useLazyExportDraftReportQuery,
} from "../model/api/api";
import { getColumns } from "../model/constants/columns";
import { IDraftReportItem } from "../model/types";
import { TableHeader } from "./components/TableHeader";

export type ApiSortItem = { field: string; direction: "asc" | "desc" };

export interface IGetDraftReportQuery
  extends Omit<IBaseQueryParams, "sort" | "order" | "order_by"> {
  agency?: string;
  partner_id?: string;
  client_id?: string;
  service?: string;
  report_month?: string;

  sort?: ApiSortItem[];
}

function toApiSort(sort?: string[] | string): ApiSortItem[] | undefined {
  const s = Array.isArray(sort) ? sort[0] : sort;
  if (!s) return undefined;

  const desc = s.startsWith("-");
  const field = desc ? s.slice(1) : s;

  if (!field) return undefined;

  return [{ field, direction: desc ? "desc" : "asc" }];
}

export function DraftReport() {
  const [allAdjustedSpends, setAllAdjustedSpends] = useState<
    { id: number; spends: string }[]
  >([]);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [partnerName] = useState("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    try {
      const draftReportColumnVisibility = localStorage.getItem(
        "draftReportColumnVisibility",
      );

      return draftReportColumnVisibility
        ? JSON.parse(draftReportColumnVisibility)
        : {};
    } catch (error) {
      enqueueSnackbar(
        "Ошибка загрузки сохранненой конфигурации видимости колонок. Применены настройки по умолчанию.",
        {
          variant: "warning",
        },
      );

      return {};
    }
  });
  const {
    query,
    filters,
    onPaginationModelChange,
    onSearch,
    onResetFilter,
    onFilter,
    activeTab,
    setActiveTab,
    sortModel,
    onSortModelChange,
  } = useGetReportsQuery({
    partnerName,
  });

  const draftQuery: Partial<IGetDraftReportQuery> = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    const { sort, order, order_by, ...rest } = query as any;

    return {
      ...rest,
      sort: toApiSort(sort),
    };
  }, [query]);
  const [exportDraftReport] = useLazyExportDraftReportQuery();
  const [searchParams] = useSearchParams();
  const { data: permissionData } = useGetPermissionsQuery(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const { data, isFetching, refetch } = useGetDraftReportsListQuery(draftQuery);
  const [updateBonusItems] = useUpdateBonusItemsMutation();
  const location = useLocation();
  const isBonusesCalculated = data?.meta.total !== 0;
  const allowedAgencies = permissionData?.data?.allowed_agencies;

  const rows = useMemo(() => {
    return (data?.data ?? []).map((item) => ({
      ...item,
      _rowId: `${item.id}_${item.project_id}_${item.account_id}_${item.client_id}_${item.contract}_${item.service?.value}`,
    }));
  }, [data?.data]);

  const handleAllAdjustmentsChange = (id: number, value: string) => {
    setAllAdjustedSpends((prev) => {
      const filtered = prev.filter((item) => item.id !== id);

      if (
        value !== data?.data.find((item) => item.id === id)?.spends_adjustment
      ) {
        return [...filtered, { id, spends: value }];
      }

      return filtered;
    });
  };

  const handleSaveAll = async () => {
    if (allAdjustedSpends.length === 0) {
      return;
    }

    setIsSavingAll(true);

    try {
      await updateBonusItems(allAdjustedSpends).unwrap();

      enqueueSnackbar("Все изменения по откруткам сохранены", {
        variant: "success",
      });

      setAllAdjustedSpends([]);
      refetch();
    } catch (error) {
      //В TypeScript при использовании catch блока, переменная ошибки по умолчанию имеет тип unknown,
      // поэтому нужно либо указать тип явно, либо использовать проверки типа.
      let errorMessage = "Не удалось обновить данные, попробуйте еще раз.";

      if (error && typeof error === "object" && "data" in error) {
        errorMessage = (error as { data: string }).data;
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      setIsSavingAll(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allAdjustedSpends.length > 0) {
        event.preventDefault();
        event.returnValue =
          "У вас есть несохраненные изменения. Вы уверены, что хотите уйти?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [allAdjustedSpends]);

  // Блокировка переходов при наличии несохраненных изменений
  const blocker = useBlocker(({ nextLocation }) => {
    return (
      allAdjustedSpends.length > 0 &&
      nextLocation.pathname !== location.pathname
    );
  });

  // Обработка блокировки
  useEffect(() => {
    if (blocker.state === "blocked" && allAdjustedSpends.length > 0) {
      const confirmNavigation = window.confirm(
        "У вас есть несохраненные изменения. Вы уверены, что хотите уйти?",
      );

      if (confirmNavigation) {
        blocker.proceed(); // Разрешаем переход
      } else {
        blocker.reset(); // Отменяем переход
      }
    }
  }, [blocker, allAdjustedSpends]);

  useEffect(() => {
    localStorage.setItem(
      "draftReportColumnVisibility",
      JSON.stringify(columnVisibilityModel),
    );
  }, [columnVisibilityModel]);

  const handleExportDraftReport = async () => {
    const report_month = searchParams.get("report_month");
    const FILE_NAME = "Предварительный_отчет_по_бонусам_Партнеров_";

    if (!report_month) {
      enqueueSnackbar("Выберите месяц для экспорта", {
        variant: "warning",
        handleClose: closeSnackbar,
      });

      return;
    }

    try {
      setIsExporting(true);

      const queryParams = {
        report_month,
      };
      const result = await exportDraftReport(queryParams);

      if (result.data) {
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement("a");
        const resultFileName = `${FILE_NAME}${report_month}`;
        link.href = url;
        link.setAttribute("download", `${resultFileName}.csv`); // Имя файла
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <TablePage<IDraftReportItem>
        handleTabsChange={setActiveTab}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        slots={{
          toolbar: TableToolbar,
        }}
        localeText={{
          toolbarColumns: "Видимость колонок",
          columnsManagementShowHideAllText: "Показать/скрыть все",
          columnsManagementReset: "Сброс",
          noRowsLabel: "Нет данных за выбранный период",
        }}
        tabsValue={activeTab}
        tabsOptions={companiesTabsOptions}
        allowedAgencies={allowedAgencies}
        loading={isFetching}
        disableTablePageHeader={isFetching}
        disableColumnSelector={false}
        title={
          <TableHeader
            handleSaveAll={handleSaveAll}
            isSavingAll={isSavingAll}
            hasChanges={Object.keys(allAdjustedSpends).length > 0}
            isBonusesCalculated={isBonusesCalculated}
          />
        }
        tableSearchModel={{
          value: query.search || "",
          onSearch,
        }}
        tableFilterModel={{
          onReset: () =>
            onResetFilter(["partner_id", "agency", "service", "report_month"]),
          onFilter,
          filters,
        }}
        rowCount={data?.meta.total || 0}
        paginationMode="server"
        columns={getColumns(
          handleAllAdjustmentsChange,
          setAllAdjustedSpends,
          refetch,
        )}
        paginationModel={{
          page: query.page ? Number(query.page) - 1 : 0,
          pageSize: query.per_page ? Number(query.per_page) : 50,
        }}
        pageSizeOptions={[25, 50, 100]}
        onPaginationModelChange={onPaginationModelChange}
        rows={rows}
        disableRowSelectionOnClick
        getRowId={(row) => row._rowId}
        exportStates={{
          handleExportDraftReport,
          isExporting,
          isSavingAll,
          hasChanges: allAdjustedSpends.length > 0,
          isBonusesCalculated,
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
      />
    </Box>
  );
}
